import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Stack,
  Paper,
  Divider,
  Tooltip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  School as ReviewIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Translate as TranslateIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Headphones as HeadphonesIcon,
  ArrowForward as ArrowForwardIcon,
  PlayCircleOutlined as PlayCircleIcon,
} from '@mui/icons-material';
import { useVocabularyWords, useVocabularyStats, useDeleteVocabularyWord, useUpdateVocabularyWord, useAddManualVocabularyWord, useReviewVocabularyWord, useRefreshVocabularyContext } from '../../hooks/useVocabulary';
import { useTrackListeningTime } from '../../hooks/useListeningTime';
import { vocabularyService } from '../../services/vocabulary.service';
import { useProjects, useProjectById } from '../../hooks/useProjects';
import { useProjectPodcasts } from '../../hooks/usePodcasts';
import { useYouTubeVideos } from '../../hooks/useYouTubeVideos';
import { useNavigate } from 'react-router';
import type { VocabularyWord, WordSegmentDetail } from '../../types/api.types';
import { YouTubeSegmentModal } from '../../components/youtube/YouTubeSegmentModal';
import { ROUTES } from '../../config/constants';
import toast from 'react-hot-toast';
import { LanguageBadge } from '../../components/vocabulary/LanguageBadge';
import { WordAudioButton } from '../../components/vocabulary/WordAudioButton';
import { useTranslateText } from '../../hooks/useTranslation';
import { decodeHtmlEntities } from '../../utils/textUtils';

export const VocabularyList = () => {
  const { t } = useTranslation('vocabulary');
  const navigate = useNavigate();
  const { projects } = useProjects();
  const [selectedProjectId, setSelectedProjectId] = useState<number | 'all'>(() => {
    const saved = sessionStorage.getItem('vocabulary-filter-project');
    if (!saved || saved === 'all') return 'all';
    const parsed = Number(saved);
    return isNaN(parsed) ? 'all' : parsed;
  });
  // Channel/podcast and episode/video filters share a unified value space:
  // - 'all'          : no filter
  // - number         : podcast ID (existing podcasts)
  // - 'yt-ch:<uuid>' : YouTube channel ID
  // - 'yt-vid:<ytId>': YouTube video ID (youtubeVideoId)
  const [selectedPodcastId, setSelectedPodcastId] = useState<number | string | 'all'>(() => {
    const saved = sessionStorage.getItem('vocabulary-filter-podcast');
    if (!saved || saved === 'all') return 'all';
    const parsed = Number(saved);
    // String values like 'yt-ch:...' are not numeric, preserve as-is
    return isNaN(parsed) ? saved : parsed;
  });
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<number | string | 'all'>(() => {
    const saved = sessionStorage.getItem('vocabulary-filter-episode');
    if (!saved || saved === 'all') return 'all';
    const parsed = Number(saved);
    // String values like 'yt-vid:...' are not numeric, preserve as-is
    return isNaN(parsed) ? saved : parsed;
  });
  const [selectedLanguage, setSelectedLanguage] = useState<string | 'all'>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('vocabulary-selected-language');
    return saved || 'all';
  });
  const [searchTerm, setSearchTerm] = useState(() => {
    return sessionStorage.getItem('vocabulary-filter-search') || '';
  });
  const [editingWord, setEditingWord] = useState<VocabularyWord | null>(null);
  const [editedTranslation, setEditedTranslation] = useState('');
  const [editedImageUrl, setEditedImageUrl] = useState('');
  const [segmentsDialogWord, setSegmentsDialogWord] = useState<VocabularyWord | null>(null);
  const [youtubeModalSegment, setYoutubeModalSegment] = useState<WordSegmentDetail | null>(null);
  const [showAddManualDialog, setShowAddManualDialog] = useState(false);
  const [reviewingWord, setReviewingWord] = useState<VocabularyWord | null>(null);
  const [reviewingIndex, setReviewingIndex] = useState<number>(-1);
  const [showReviewAnswer, setShowReviewAnswer] = useState(false);
  const [playingSegmentId, setPlayingSegmentId] = useState<number | null>(null);
  const [audioInstance, setAudioInstance] = useState<HTMLAudioElement | null>(null);
  const [manualWordForm, setManualWordForm] = useState({
    word: '',
    translation: '',
    context: '',
    imageUrl: '',
    sourceLanguage: '',
    targetLanguage: '',
  });

  // Fetch podcasts for the selected project
  const { data: podcastsMap, isLoading: isPodcastsLoading } = useProjectPodcasts(
    selectedProjectId === 'all' ? 0 : selectedProjectId
  );

  // Fetch YouTube videos for the selected project (for channel/video filter)
  const { data: ytVideos } = useYouTubeVideos(
    selectedProjectId === 'all' ? 0 : selectedProjectId
  );

  // Get available podcasts for selected project
  const availablePodcasts = useMemo(() => {
    if (selectedProjectId === 'all') return [];
    const project = projects.find(p => p.id === selectedProjectId);
    return project?.podcastEpisodes || [];
  }, [selectedProjectId, projects]);

  // Get available episodes (podcast) or videos (YouTube) for the selected channel/podcast
  const availableEpisodes = useMemo(() => {
    if (selectedPodcastId === 'all') return [] as { id: number | string; name: string }[];
    if (typeof selectedPodcastId === 'string' && selectedPodcastId.startsWith('yt-ch:')) {
      const channelId = selectedPodcastId.slice(6);
      return (ytVideos || [])
        .filter(v => v.channel_id === channelId)
        .map(v => ({ id: `yt-vid:${v.youtube_video_id}`, name: v.title }));
    }
    return availablePodcasts
      .filter(ep => ep.podcastId === (selectedPodcastId as number))
      .map(ep => ({ id: ep.id as number | string, name: ep.name }));
  }, [selectedPodcastId, availablePodcasts, ytVideos]);

  // Get unique podcasts from available episodes using actual podcast data
  const uniquePodcasts = useMemo(() => {
    if (!podcastsMap) return [];
    return Object.values(podcastsMap).map(podcast => ({
      id: podcast.podId,
      name: podcast.name,
    }));
  }, [podcastsMap]);

  // Get unique YouTube channels for the selected project
  const uniqueYouTubeChannels = useMemo(() => {
    if (!ytVideos) return [];
    const seen = new Set<string>();
    const channels: { id: string; name: string }[] = [];
    for (const v of ytVideos) {
      if (v.channel && !seen.has(v.channel_id)) {
        seen.add(v.channel_id);
        channels.push({ id: v.channel_id, name: v.channel.name });
      }
    }
    return channels;
  }, [ytVideos]);

  // Reset podcast filter when project changes
  const handleProjectChange = (projectId: number | 'all') => {
    setSelectedProjectId(projectId);
    setSelectedPodcastId('all');
    setSelectedEpisodeId('all');
  };

  // Reset episode/video filter when channel/podcast changes
  const handlePodcastChange = (podcastId: number | string | 'all') => {
    setSelectedPodcastId(podcastId);
    setSelectedEpisodeId('all');
  };

  const projectIdFilter = selectedProjectId === 'all' ? undefined : selectedProjectId;
  const languageFilter = selectedLanguage === 'all' ? undefined : selectedLanguage;
  
  // First fetch ALL words to determine available languages
  const { data: allWords } = useVocabularyWords(projectIdFilter, undefined);
  
  // Then fetch filtered words based on selected language
  const { data: words, isLoading, error } = useVocabularyWords(projectIdFilter, languageFilter);
  const { data: stats } = useVocabularyStats(projectIdFilter, languageFilter);
  const { mutate: deleteWord } = useDeleteVocabularyWord();
  const { mutate: updateWord, isPending: isUpdating } = useUpdateVocabularyWord();
  const { mutate: addManualWord, isPending: isAddingManualWord } = useAddManualVocabularyWord();
  useReviewVocabularyWord(); // keep query cache wired; mutations happen in VocabularyReview
  const { mutate: refreshContext, isPending: isRefreshing } = useRefreshVocabularyContext();
  const { mutate: trackListeningTime } = useTrackListeningTime();
  const playStartRef = useRef<number | null>(null);
  const translateText = useTranslateText();
  
  // Fetch project for the editing word to get language codes
  const editingWordProjectId = editingWord?.segments?.[0]?.projectId;
  const { data: editingWordProject } = useProjectById(editingWordProjectId || 0);

  // Get unique source languages from actual vocabulary words
  const uniqueSourceLanguages = useMemo(() => {
    if (!allWords) return [];
    const sourceSet = new Set(allWords.map(w => w.sourceLanguage));
    return Array.from(sourceSet).sort();
  }, [allWords]);

  // Auto-select language if user only has words in one language
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  useEffect(() => {
    if (!hasAutoSelected && uniqueSourceLanguages.length === 1 && selectedLanguage === 'all') {
      const language = uniqueSourceLanguages[0];
      setSelectedLanguage(language);
      localStorage.setItem('vocabulary-selected-language', language);
      setHasAutoSelected(true);
    }
  }, [uniqueSourceLanguages, selectedLanguage, hasAutoSelected]);

  // Persist language selection to localStorage whenever it changes
  useEffect(() => {
    if (selectedLanguage !== 'all') {
      localStorage.setItem('vocabulary-selected-language', selectedLanguage);
    } else {
      localStorage.removeItem('vocabulary-selected-language');
    }
  }, [selectedLanguage]);

  // Persist filter selections to sessionStorage so navigating to transcript and back
  // restores the list exactly as left (React Query keeps the data; we keep the filters).
  useEffect(() => {
    sessionStorage.setItem('vocabulary-filter-project', String(selectedProjectId));
  }, [selectedProjectId]);

  useEffect(() => {
    sessionStorage.setItem('vocabulary-filter-podcast', String(selectedPodcastId));
  }, [selectedPodcastId]);

  useEffect(() => {
    sessionStorage.setItem('vocabulary-filter-episode', String(selectedEpisodeId));
  }, [selectedEpisodeId]);

  useEffect(() => {
    if (searchTerm) {
      sessionStorage.setItem('vocabulary-filter-search', searchTerm);
    } else {
      sessionStorage.removeItem('vocabulary-filter-search');
    }
  }, [searchTerm]);

  // Get unique target languages from projects for manual word creation
  const uniqueTargetLanguages = useMemo(() => {
    const targetSet = new Set(projects.map(p => p.userLanguage));
    return Array.from(targetSet).sort();
  }, [projects]);

  const handleDeleteWord = (wordId: number) => {
    if (confirm(t('deleteConfirm'))) {
      deleteWord(wordId);
    }
  };

  const handleEditWord = (word: VocabularyWord) => {
    setEditingWord(word);
    setEditedTranslation(word.translation);
    setEditedImageUrl(word.imageUrl || '');
  };

  const handleSaveEdit = () => {
    if (editingWord && editedTranslation.trim()) {
      updateWord({
        wordId: editingWord.id,
        payload: { 
          translation: editedTranslation,
          imageUrl: editedImageUrl || undefined,
        },
      }, {
        onSuccess: () => {
          setEditingWord(null);
          setEditedTranslation('');
          setEditedImageUrl('');
        },
      });
    }
  };

  const handleTranslateWord = () => {
    if (!editingWord || !editingWordProject) return;

    // Map language names to ISO codes
    const languageToCode: Record<string, string> = {
      'japanese': 'ja',
      'english': 'en',
      'spanish': 'es',
      'french': 'fr',
      'german': 'de',
      'italian': 'it',
      'portuguese': 'pt',
      'chinese': 'zh',
      'korean': 'ko',
      'russian': 'ru',
      'turkish': 'tr',
    };

    const getLanguageCode = (lang: string) => {
      const lower = lang.toLowerCase();
      // If it already looks like a code (2-3 chars or has hyphen), extract the ISO code
      if (lower.includes('-')) {
        return lower.split('-')[0];
      }
      // Otherwise map the full name to code
      return languageToCode[lower] || lower;
    };

    const sourceLanguage = getLanguageCode(editingWord.sourceLanguage);
    const targetLanguage = getLanguageCode(editingWord.targetLanguage);

    translateText.mutate({
      text: editingWord.word,
      sourceLanguage,
      targetLanguage,
    }, {
      onSuccess: (data) => {
        setEditedTranslation(decodeHtmlEntities(data.translatedText));
        toast.success('Translation complete!');
      },
      onError: (error) => {
        console.error('Translation error:', error);
        toast.error('Translation failed');
      },
    });
  };

  const handleStartReview = () => {
    const languageParam = selectedLanguage !== 'all' ? `?language=${selectedLanguage}` : '';
    navigate(`${ROUTES.VOCABULARY_REVIEW}${languageParam}`);
  };

  const handleShowSegments = (word: VocabularyWord) => {
    setSegmentsDialogWord(word);
  };

  const handleNavigateToSegment = (segment: WordSegmentDetail) => {
    if (segment.youtubeVideoId) {
      // Open YouTube segment in the inline modal (no navigation needed)
      setSegmentsDialogWord(null);
      setYoutubeModalSegment(segment);
    } else {
      // Navigate to podcast player
      navigate(`/player/${segment.projectId}/${segment.episodeId}?segmentId=${segment.segmentId}`);
      setSegmentsDialogWord(null);
    }
  };

  const handleAddManualWord = () => {
    setShowAddManualDialog(true);
    // Auto-detect languages from first project if available
    if (projects.length > 0) {
      const firstProject = projects[0];
      setManualWordForm(prev => ({
        ...prev,
        sourceLanguage: firstProject.projectLanguage,
        targetLanguage: firstProject.userLanguage,
      }));
    }
  };

  const handleSaveManualWord = () => {
    if (!manualWordForm.word.trim() || !manualWordForm.translation.trim() || !manualWordForm.sourceLanguage || !manualWordForm.targetLanguage) {
      alert(t('addManualDialog.required'));
      return;
    }

    addManualWord(manualWordForm, {
      onSuccess: () => {
        setShowAddManualDialog(false);
        setManualWordForm({
          word: '',
          translation: '',
          context: '',
          imageUrl: '',
          sourceLanguage: '',
          targetLanguage: '',
        });
      },
    });
  };

  const handleCancelAddManual = () => {
    setShowAddManualDialog(false);
    setManualWordForm({
      word: '',
      translation: '',
      context: '',
      imageUrl: '',
      sourceLanguage: '',
      targetLanguage: '',
    });
  };

  const stopAudio = () => {
    if (audioInstance) {
      audioInstance.pause();
      audioInstance.currentTime = 0;
    }
    setPlayingSegmentId(null);
    setAudioInstance(null);
  };

  const handlePlaySegment = (segmentId: number, audioUrl: string, startTime: number, endTime: number, audioOffset?: number, language?: string) => {
    const reportListeningTime = () => {
      if (playStartRef.current !== null) {
        const seconds = Math.floor((performance.now() - playStartRef.current) / 1000);
        playStartRef.current = null;
        if (seconds > 0 && language) {
          trackListeningTime({ seconds, language });
        }
      }
    };

    if (playingSegmentId === segmentId && audioInstance) {
      reportListeningTime();
      stopAudio();
      return;
    }
    if (audioInstance) {
      reportListeningTime();
      audioInstance.pause();
      audioInstance.currentTime = 0;
    }
    const offset = audioOffset || 0;
    const adjustedStart = startTime + offset;
    const adjustedEnd = endTime + offset;
    const audio = new Audio(`${import.meta.env.VITE_API_BASE_URL}/audio/proxy?url=${encodeURIComponent(audioUrl)}`);
    audio.currentTime = adjustedStart;
    audio.play();
    playStartRef.current = performance.now();
    setPlayingSegmentId(segmentId);
    setAudioInstance(audio);
    const handleTimeUpdate = () => {
      if (audio.currentTime >= adjustedEnd) {
        audio.pause();
        reportListeningTime();
        setPlayingSegmentId(null);
        setAudioInstance(null);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
      }
    };
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', () => { reportListeningTime(); setPlayingSegmentId(null); setAudioInstance(null); });
    audio.addEventListener('error', () => { playStartRef.current = null; setPlayingSegmentId(null); setAudioInstance(null); });
  };

  const formatSegmentTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleReviewCard = (word: VocabularyWord) => {
    const idx = filteredWords.findIndex(w => w.id === word.id);
    setReviewingWord(word);
    setReviewingIndex(idx);
    setShowReviewAnswer(false);
  };

  const handleNextCard = () => {
    if (filteredWords.length === 0) return;
    stopAudio();
    const nextIndex = (reviewingIndex + 1) % filteredWords.length;
    setReviewingWord(filteredWords[nextIndex]);
    setReviewingIndex(nextIndex);
    setShowReviewAnswer(false);
  };

  const handlePrevCard = () => {
    if (filteredWords.length === 0) return;
    stopAudio();
    const prevIndex = (reviewingIndex - 1 + filteredWords.length) % filteredWords.length;
    setReviewingWord(filteredWords[prevIndex]);
    setReviewingIndex(prevIndex);
    setShowReviewAnswer(false);
  };

  const handleShowReviewAnswer = () => {
    setShowReviewAnswer(true);
  };

  const handleCancelReview = () => {
    stopAudio();
    setReviewingWord(null);
    setReviewingIndex(-1);
    setShowReviewAnswer(false);
  };

  // Filter words by search term, podcast, and episode
  const filteredWords = useMemo(() => {
    if (!words) return [];
    
    let filtered = words;

    // Filter by channel/podcast
    if (selectedPodcastId !== 'all') {
      if (typeof selectedPodcastId === 'string' && selectedPodcastId.startsWith('yt-ch:')) {
        // YouTube channel: match any segment whose youtubeVideoId belongs to this channel
        const channelId = selectedPodcastId.slice(6);
        const channelVideoIds = new Set(
          (ytVideos || []).filter(v => v.channel_id === channelId).map(v => v.youtube_video_id)
        );
        filtered = filtered.filter(word =>
          word.segments?.some(seg => seg.youtubeVideoId != null && channelVideoIds.has(seg.youtubeVideoId))
        );
      } else {
        filtered = filtered.filter(word =>
          word.segments?.some(segment => segment.podcastId === selectedPodcastId)
        );
      }
    }

    // Filter by episode/video
    if (selectedEpisodeId !== 'all') {
      if (typeof selectedEpisodeId === 'string' && selectedEpisodeId.startsWith('yt-vid:')) {
        // YouTube video: match by youtubeVideoId
        const ytVideoId = selectedEpisodeId.slice(7);
        filtered = filtered.filter(word =>
          word.segments?.some(seg => seg.youtubeVideoId === ytVideoId)
        );
      } else {
        filtered = filtered.filter(word =>
          word.segments?.some(segment => segment.episodeId === selectedEpisodeId)
        );
      }
    }
    
    // Filter by search term
    if (searchTerm && searchTerm.trim() !== '') {
      const normalizedSearchTerm = searchTerm.trim().normalize('NFC');
      const stripHtml = (text: string) => text.replace(/<[^>]*>/g, '');
      
      filtered = filtered.filter(word => {
        const normalizedWord = stripHtml(word.word).normalize('NFC');
        const normalizedTranslation = stripHtml(word.translation).normalize('NFC');
        
        return normalizedWord.includes(normalizedSearchTerm) ||
          normalizedTranslation.includes(normalizedSearchTerm);
      });
    }
    
    // Sorting is now handled by the database query for better performance
    return filtered;
  }, [words, searchTerm, selectedPodcastId, selectedEpisodeId]);

  // Compute top episodes by number of due words — used to suggest review prep
  const topDueEpisodes = useMemo(() => {
    if (!words || words.length === 0) return [];

    // Midnight of tomorrow in the user's local timezone — matches the backend timezone param
    const now = new Date();
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);

    // Key: "yt:<youtubeVideoId>" for YouTube segments, "ep:<episodeId>" for podcast segments.
    // This avoids the key=0 collision where all YouTube segments share episodeId=0.
    const episodeMap = new Map<string, {
      key: string;
      episodeId: number;
      projectId: number;
      episodeName: string;
      podcastName: string;
      youtubeVideoId?: string;
      youtubeSavedVideoId?: string;
      count: number;
    }>();

    for (const word of words) {
      const isDue = !word.nextReviewDate || new Date(word.nextReviewDate) < endOfDay;
      if (!isDue || !word.segments || word.segments.length === 0) continue;

      // Count each unique episode/video once per word
      const seenKeys = new Set<string>();
      for (const segment of word.segments) {
        const isYouTube = !!segment.youtubeVideoId;
        const mapKey = isYouTube
          ? `yt:${segment.youtubeVideoId}`
          : `ep:${segment.episodeId}`;

        if (seenKeys.has(mapKey)) continue;
        seenKeys.add(mapKey);

        if (!episodeMap.has(mapKey)) {
          episodeMap.set(mapKey, {
            key: mapKey,
            episodeId: segment.episodeId,
            projectId: Number(segment.projectId),
            // For YouTube segments use the video title; for podcasts use the episode name.
            episodeName: isYouTube
              ? (segment.youtubeTitle ?? '')
              : segment.episodeName,
            podcastName: segment.podcastName,
            youtubeVideoId: segment.youtubeVideoId,
            youtubeSavedVideoId: segment.youtubeSavedVideoId,
            count: 0,
          });
        }
        episodeMap.get(mapKey)!.count++;
      }
    }

    return Array.from(episodeMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 2);
  }, [words]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return t('never');
    return new Date(dateStr).toLocaleDateString();
  };

  const getDifficultyColor = (level: number) => {
    if (level === 0) return 'default';
    if (level <= 2) return 'warning';
    if (level <= 4) return 'info';
    return 'success';
  };

  const getDifficultyLabel = (level: number) => {
    if (level === 0) return t('difficulty.new');
    if (level <= 2) return t('difficulty.learning');
    if (level <= 4) return t('difficulty.practicing');
    return t('difficulty.mastered');
  };

  const getExposureTierChip = (tier?: string) => {
    if (!tier) return null;
    const config: Record<string, { label: string; color: 'success' | 'primary' | 'warning' | 'default' | 'secondary' }> = {
      strong:   { label: t('tier.strong'),   color: 'success' },
      building: { label: t('tier.building'), color: 'primary' },
      low:      { label: t('tier.low'),      color: 'warning' },
      single:   { label: t('tier.single'),   color: 'default' },
      phrase:   { label: t('tier.phrase'),   color: 'secondary' },
    };
    const c = config[tier];
    if (!c) return null;
    return <Chip label={c.label} size="small" color={c.color} variant="outlined" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1">
          {t('vocabulary')}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddManualWord}
          >
            {t('addNewWord')}
          </Button>
          <Button
            variant="contained"
            startIcon={<ReviewIcon />}
            onClick={handleStartReview}
            disabled={!stats || stats.dueForReview === 0}
          >
            {t('review')} ({stats?.dueForReview || 0})
          </Button>
        </Stack>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.totalWords}</Typography>
              <Typography variant="caption" color="text.secondary">{t('stats.totalWords')}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{stats.dueForReview}</Typography>
              <Typography variant="caption" color="text.secondary">{t('stats.dueToday')}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.masteredWords}</Typography>
              <Typography variant="caption" color="text.secondary">{t('stats.mastered')}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">{stats.learningWords}</Typography>
              <Typography variant="caption" color="text.secondary">{t('stats.learning')}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{stats.newWords}</Typography>
              <Typography variant="caption">{t('stats.new')}</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {uniqueSourceLanguages.length > 1 && (
            <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('filters.filterByLanguage')}</InputLabel>
                <Select
                  value={selectedLanguage}
                  label={t('filters.filterByLanguage')}
                  onChange={(e) => setSelectedLanguage(e.target.value as string | 'all')}
                >
                  <MenuItem value="all">{t('filters.allLanguages')}</MenuItem>
                  {uniqueSourceLanguages.map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      {lang}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: uniqueSourceLanguages.length > 1 ? 2.4 : 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('filters.filterByProject')}</InputLabel>
              <Select
                value={selectedProjectId}
                label={t('filters.filterByProject')}
                onChange={(e) => handleProjectChange(e.target.value as number | 'all')}
              >
                <MenuItem value="all">{t('filters.allProjects')}</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.projectName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: uniqueSourceLanguages.length > 1 ? 2.4 : 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('filters.filterByPodcast')}</InputLabel>
              <Select
                value={selectedPodcastId}
                label={t('filters.filterByPodcast')}
                onChange={(e) => handlePodcastChange(e.target.value as number | string | 'all')}
                disabled={selectedProjectId === 'all'}
              >
                {isPodcastsLoading ? (
                  <MenuItem value="all">
                    {t('loading')}
                  </MenuItem>
                ) : (uniquePodcasts.length === 0 && uniqueYouTubeChannels.length === 0) ? (
                  <MenuItem value="all">
                    {selectedProjectId === 'all'
                      ? t('filters.selectProjectFirst')
                      : t('filters.noEpisodesInProject')
                    }
                  </MenuItem>
                ) : [
                  <MenuItem key="all" value="all">{t('filters.allPodcasts')}</MenuItem>,
                  ...uniquePodcasts.map((podcast) => (
                    <MenuItem key={`p:${podcast.id}`} value={podcast.id}>
                      {podcast.name}
                    </MenuItem>
                  )),
                  ...uniqueYouTubeChannels.map((channel) => (
                    <MenuItem key={`yt-ch:${channel.id}`} value={`yt-ch:${channel.id}`}>
                      {channel.name}
                    </MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: uniqueSourceLanguages.length > 1 ? 2.4 : 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('filters.filterByEpisode')}</InputLabel>
              <Select
                value={selectedEpisodeId}
                label={t('filters.filterByEpisode')}
                onChange={(e) => setSelectedEpisodeId(e.target.value as number | string | 'all')}
                disabled={selectedPodcastId === 'all'}
              >
                {availableEpisodes.length === 0 ? (
                  <MenuItem value="all">
                    {selectedPodcastId === 'all'
                      ? t('filters.selectPodcastFirst')
                      : t('filters.noEpisodesFound')
                    }
                  </MenuItem>
                ) : [
                  <MenuItem key="all" value="all">{t('filters.allEpisodes')}</MenuItem>,
                  ...availableEpisodes.map((episode) => (
                    <MenuItem key={String(episode.id)} value={episode.id}>
                      {episode.name || 'Untitled Episode'}
                    </MenuItem>
                  ))
                ]}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('filters.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Review Prep — top episodes with due words */}
      {topDueEpisodes.length > 0 && (stats?.dueForReview ?? 0) > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            borderColor: 'primary.main',
            borderWidth: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <HeadphonesIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
              {t('reviewPrep.title', 'Review Prep')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              {t('reviewPrep.subtitle', '— listen to these episodes before your review')}
            </Typography>
          </Box>
          <Stack spacing={1}>
            {topDueEpisodes.map((ep) => (
              <Box
                key={ep.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight="medium" noWrap>
                    {ep.episodeName || t('reviewPrep.untitledEpisode', 'Untitled Episode')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {ep.podcastName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Chip
                    label={t('reviewPrep.wordsDue', '{{count}} words due', { count: ep.count })}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => {
                      if (ep.youtubeSavedVideoId) {
                        navigate(`/projects/${ep.projectId}/videos/${ep.youtubeSavedVideoId}`);
                      } else {
                        navigate(`/player/${ep.projectId}/${ep.episodeId}?focus=true`);
                      }
                    }}
                  >
                    {t('reviewPrep.goToEpisode', 'Go to Episode')}
                  </Button>
                </Box>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('error')}
        </Alert>
      )}

      {/* Empty State */}
      {!isLoading && filteredWords.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? t('empty.noWordsFound') : t('empty.noWordsYet')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm 
              ? t('empty.tryDifferentSearch')
              : t('empty.startAdding')
            }
          </Typography>
        </Paper>
      )}

      {/* Word Cards Grid */}
      {!isLoading && filteredWords.length > 0 && (
        <Grid container spacing={2}>
          {filteredWords.map((word) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={word.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => handleReviewCard(word)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h5" component="div" sx={{ wordBreak: 'break-word' }}>
                      {word.word}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <LanguageBadge language={word.sourceLanguage} />
                      <Chip
                        label={getDifficultyLabel(word.difficultyLevel)}
                        size="small"
                        color={getDifficultyColor(word.difficultyLevel)}
                      />
                      {getExposureTierChip(word.exposureTier)}
                    </Stack>
                  </Box>

                  <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontStyle: 'italic' }}>
                    {word.translation}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('contextLabel')} {word.context.length > 100 ? word.context.substring(0, 100) + '...' : word.context}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('reviews')} {word.reviewCount} ({word.correctCount} {t('correct')})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('lastReviewed')} {formatDate(word.lastReviewedAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('nextReview')} {formatDate(word.nextReviewDate)}
                      </Typography>
                      {word.segments && word.segments.length > 0 && (
                        <Typography 
                          variant="caption" 
                          color="primary" 
                          sx={{ 
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': { color: 'primary.dark' }
                          }}
                          onClick={(e) => { e.stopPropagation(); handleShowSegments(word); }}
                        >
                          {t('appearsIn', { count: word.segments.length })}
                        </Typography>
                      )}
                    </Stack>
                    {word.imageUrl && (
                      <Box
                        component="img"
                        src={word.imageUrl}
                        alt={word.word}
                        sx={{
                          width: 100,
                          height: 100,
                          borderRadius: 1,
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </Box>
                </CardContent>

                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                  <Tooltip title={t('editTranslation')}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditWord(word); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('deleteWord')}>
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteWord(word.id); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Word Dialog */}
      <Dialog
        open={!!editingWord}
        onClose={() => setEditingWord(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('editDialog.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('editDialog.wordLabel')} <strong>{editingWord?.word}</strong>
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={translateText.isPending ? <CircularProgress size={16} /> : <TranslateIcon />}
              onClick={handleTranslateWord}
              disabled={translateText.isPending || !editingWordProject}
              sx={{ display: 'none' }} // Hidden - remove this line to re-enable
            >
              {t('editDialog.translateButton', 'Translate')}
            </Button>
          </Box>
          <TextField
            autoFocus
            fullWidth
            label={t('editDialog.translationLabel')}
            value={editedTranslation}
            onChange={(e) => setEditedTranslation(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('editDialog.imageUrlLabel')}
            placeholder={t('editDialog.imageUrlPlaceholder')}
            value={editedImageUrl}
            onChange={(e) => setEditedImageUrl(e.target.value)}
            helperText={t('editDialog.imageUrlHelp')}
            sx={{ mt: 2 }}
          />
          
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('editDialog.contextLabel', 'Context')}
              </Typography>
              <Button
                size="small"
                startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={async () => {
                  if (!editingWord) return;
                  refreshContext(editingWord.id, {
                    onSuccess: async () => {
                      // Wait a bit for the query invalidation to trigger refetch
                      await new Promise(resolve => setTimeout(resolve, 100));
                      // Fetch the updated word data
                      const updatedWords = await vocabularyService.getWords(projectIdFilter, languageFilter);
                      const updatedWord = updatedWords.find(w => w.id === editingWord.id);
                      if (updatedWord) {
                        setEditingWord(updatedWord);
                      }
                    },
                  });
                }}
                disabled={isRefreshing}
              >
                {t('editDialog.refreshContext', 'Refresh from transcript')}
              </Button>
            </Box>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 150, overflow: 'auto' }}>
              <Typography variant="body2" color="text.secondary">
                {editingWord?.context || t('editDialog.noContext', 'No context available')}
              </Typography>
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('editDialog.contextHelp', 'Context is the sentence where this word appeared. Click "Refresh" to update it from the current transcript.')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingWord(null)}>{t('editDialog.cancel')}</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={isUpdating || !editedTranslation.trim()}
          >
            {isUpdating ? <CircularProgress size={24} /> : t('editDialog.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Segments Dialog */}
      <Dialog
        open={!!segmentsDialogWord}
        onClose={() => setSegmentsDialogWord(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('segmentsDialog.title', { word: segmentsDialogWord?.word })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {t('segmentsDialog.description')}
          </Typography>
          <Paper variant="outlined" sx={{ mt: 2 }}>
            {segmentsDialogWord?.segments?.map((segment, index) => (
              <Box key={index}>
                <Box
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  onClick={() => handleNavigateToSegment(segment)}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {segment.projectName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {segment.youtubeVideoId ? (
                        <Stack component="span" direction="row" spacing={0.5} alignItems="center" display="inline-flex">
                          <PlayCircleIcon fontSize="inherit" color="error" />
                          <span>{segment.youtubeTitle || t('segmentsDialog.youtubeVideo', 'YouTube Video')}</span>
                        </Stack>
                      ) : (
                        <>{t('segmentsDialog.episode')} {segment.episodeName}</>
                      )}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Chip 
                        label={`${Math.floor(segment.startTime / 60)}:${String(Math.floor(segment.startTime % 60)).padStart(2, '0')} - ${Math.floor(segment.endTime / 60)}:${String(Math.floor(segment.endTime % 60)).padStart(2, '0')}`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip 
                        label={segment.sourceType === 'transcript' ? t('segmentsDialog.fromTranscript') : t('segmentsDialog.fromTranslation')}
                        size="small"
                        color={segment.sourceType === 'transcript' ? 'primary' : 'secondary'}
                      />
                    </Stack>
                  </Stack>
                </Box>
                {index < (segmentsDialogWord?.segments?.length || 0) - 1 && <Divider />}
              </Box>
            ))}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSegmentsDialogWord(null)}>{t('segmentsDialog.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Add Manual Word Dialog */}
      <Dialog
        open={showAddManualDialog}
        onClose={handleCancelAddManual}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('addManualDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
            {t('addManualDialog.description')}
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              label={t('word')}
              placeholder={t('addManualDialog.wordPlaceholder')}
              value={manualWordForm.word}
              onChange={(e) => setManualWordForm({ ...manualWordForm, word: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label={t('translation')}
              placeholder={t('addManualDialog.translationPlaceholder')}
              value={manualWordForm.translation}
              onChange={(e) => setManualWordForm({ ...manualWordForm, translation: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label={t('addManualDialog.contextLabel')}
              placeholder={t('addManualDialog.contextPlaceholder')}
              multiline
              rows={3}
              value={manualWordForm.context}
              onChange={(e) => setManualWordForm({ ...manualWordForm, context: e.target.value })}
              helperText={t('addManualDialog.contextHelp')}
            />
            <TextField
              fullWidth
              label={t('addManualDialog.imageUrlLabel')}
              placeholder={t('addManualDialog.imageUrlPlaceholder')}
              value={manualWordForm.imageUrl}
              onChange={(e) => setManualWordForm({ ...manualWordForm, imageUrl: e.target.value })}
              helperText={t('addManualDialog.imageUrlHelp')}
            />
            <FormControl fullWidth required>
              <InputLabel>{t('addManualDialog.sourceLanguage')}</InputLabel>
              <Select
                value={manualWordForm.sourceLanguage}
                label={t('addManualDialog.sourceLanguage')}
                onChange={(e) => setManualWordForm({ ...manualWordForm, sourceLanguage: e.target.value })}
              >
                <MenuItem value="">{t('addManualDialog.selectLanguage')}</MenuItem>
                {uniqueSourceLanguages.map((language) => (
                  <MenuItem key={language} value={language}>
                    {language}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>{t('addManualDialog.targetLanguage')}</InputLabel>
              <Select
                value={manualWordForm.targetLanguage}
                label={t('addManualDialog.targetLanguage')}
                onChange={(e) => setManualWordForm({ ...manualWordForm, targetLanguage: e.target.value })}
              >
                <MenuItem value="">{t('addManualDialog.selectLanguage')}</MenuItem>
                {uniqueTargetLanguages.map((language) => (
                  <MenuItem key={language} value={language}>
                    {language}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAddManual}>{t('addManualDialog.cancel')}</Button>
          <Button
            onClick={handleSaveManualWord}
            variant="contained"
            disabled={isAddingManualWord}
          >
            {isAddingManualWord ? <CircularProgress size={24} /> : t('addManualDialog.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Card Dialog */}
      <Dialog
        open={!!reviewingWord}
        onClose={handleCancelReview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReviewIcon />
          {t('reviewDialog.title')}
        </DialogTitle>
        <DialogContent>
          {reviewingWord && (
            <Box sx={{ py: 2 }}>
              {/* Word to review */}
              <Paper 
                elevation={0}
                sx={{ 
                  p: 4,
                  mb: 3,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                    {reviewingWord.word}
                  </Typography>
                  <WordAudioButton
                    word={reviewingWord.word}
                    language={reviewingWord.sourceLanguage}
                    sx={{ color: 'inherit' }}
                  />
                </Box>
                {reviewingWord.context && (
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 2, fontStyle: 'italic' }}>
                    {reviewingWord.context}
                  </Typography>
                )}
              </Paper>

              {/* Show answer button or answer */}
              {!showReviewAnswer ? (
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<VisibilityIcon />}
                    onClick={handleShowReviewAnswer}
                  >
                    {t('reviewDialog.showAnswer')}
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Translation answer */}
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 3,
                      mb: 3,
                      bgcolor: 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider',
                      textAlign: 'center',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 'medium', color: 'success.dark' }}>
                      {reviewingWord.translation}
                    </Typography>
                  </Paper>

                  {/* Image display */}
                  {reviewingWord.imageUrl && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                      <Box
                        component="img"
                        src={reviewingWord.imageUrl}
                        alt={reviewingWord.word}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: 300,
                          borderRadius: 2,
                          objectFit: 'contain',
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </Box>
                  )}

                  {/* Segment Audio Player */}
                  {reviewingWord.segments && reviewingWord.segments.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Hear in context
                      </Typography>
                      <List dense disablePadding>
                        {reviewingWord.segments.map((segment) => (
                          <ListItem
                            key={segment.segmentId}
                            disablePadding
                            sx={{ bgcolor: 'background.default', borderRadius: 1, mb: 0.5, px: 1 }}
                            secondaryAction={
                              segment.youtubeVideoId ? (
                                <IconButton
                                  edge="end"
                                  onClick={() => setYoutubeModalSegment(segment)}
                                  color="error"
                                  title="Play in context"
                                >
                                  <PlayCircleIcon />
                                </IconButton>
                              ) : (
                                <IconButton
                                  edge="end"
                                  onClick={() => handlePlaySegment(segment.segmentId, segment.audioUrl, segment.startTime, segment.endTime, segment.audioOffset, reviewingWord.sourceLanguage)}
                                  color={playingSegmentId === segment.segmentId ? 'primary' : 'default'}
                                >
                                  {playingSegmentId === segment.segmentId ? <PauseIcon /> : <PlayIcon />}
                                </IconButton>
                              )
                            }
                          >
                            <ListItemText
                              primary={
                                segment.youtubeVideoId
                                  ? (segment.youtubeTitle || 'YouTube Video')
                                  : `${segment.projectName} — ${segment.episodeName}`
                              }
                              secondary={`${formatSegmentTime(segment.startTime)} – ${formatSegmentTime(segment.endTime)}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Word stats */}
                  <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('difficulty.label')}: {getDifficultyLabel(reviewingWord.difficultyLevel)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('reviews')}: {reviewingWord.reviewCount} ({reviewingWord.correctCount} {t('correct')})
                    </Typography>
                  </Paper>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button onClick={handleCancelReview}>{t('reviewDialog.close')}</Button>
          {filteredWords.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button variant="outlined" size="small" onClick={handlePrevCard}>
                ← {t('reviewDialog.prev', 'Prev')}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {reviewingIndex + 1} / {filteredWords.length}
              </Typography>
              <Button variant="outlined" size="small" onClick={handleNextCard}>
                {t('reviewDialog.next', 'Next')} →
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* YouTube Segment Modal */}
      <YouTubeSegmentModal
        open={!!youtubeModalSegment}
        onClose={() => setYoutubeModalSegment(null)}
        youtubeVideoId={youtubeModalSegment?.youtubeVideoId ?? ''}
        startTime={youtubeModalSegment?.startTime ?? 0}
        endTime={youtubeModalSegment?.endTime ?? 0}
        videoTitle={youtubeModalSegment?.youtubeTitle ?? 'YouTube Video'}
      />
    </Box>
  );
};
